// Content-type options shown in the Create Visual Page dropdown.
// This is the shared single source of truth — reused by AddVisualPage,
// ManageVisualPages, and anywhere else the app needs to render a friendly
// label for a stored page_type value.
//
// Rationale (see the earlier investigation): there is no backend endpoint
// that exposes page-type values, and node_pages.type is a free-form string
// column with no reference table. The set below is the current user-facing
// choice — currently only Course and Conference. 'visual' is deliberately
// excluded — that's the storage type of every row in visual_pages, not a
// user-facing content type choice.

export const VISUAL_PAGE_TYPES = [
  { value: 'course',     label: 'Course' },
  { value: 'conference', label: 'Conference' },
];

// Lookup helper: returns the human label for a stored page_type value,
// falling back to the raw value (title-cased) so unknown / legacy rows
// still render something readable.
export function visualPageTypeLabel(value) {
  if (!value) return '';
  const hit = VISUAL_PAGE_TYPES.find((t) => t.value === value);
  if (hit) return hit.label;
  return String(value)
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');
}

// Validation helper used at the create endpoint boundary and by any future
// consumer that needs to reject unrecognised values.
export function isValidVisualPageType(value) {
  return VISUAL_PAGE_TYPES.some((t) => t.value === value);
}

// Maps a recorded source-page path (e.g. "courses/athlete-management-…" or
// "conferences/annual-summit") to its visual page_type. The courses/ vs
// conferences/ URL prefix is the app's canonical type signal — the exact rule
// AddVisualPage.rowMatchesType uses to sort source rows by type. Accepts the
// singular or plural prefix and any leading slash. Returns '' when the path
// carries neither prefix (e.g. a bare "node/505/edit").
export function pageTypeFromSourcePath(path) {
  const p = String(path || '').replace(/^\/+/, '');
  if (/^courses?(\/|$)/.test(p)) return 'course';
  if (/^conferences?(\/|$)/.test(p)) return 'conference';
  return '';
}

// Derive a visual page's page_type ('course' | 'conference') from authoritative
// data — used by Save-as-New so legacy visual_pages rows whose stored page_type
// is blank still resolve a valid value the backend accepts. Never guesses:
// returns '' when the data cannot reliably determine the type, and the caller
// reports what is missing rather than defaulting.
//
// Priority (most authoritative first):
//   1. An already-valid stored page_type is preserved verbatim.
//   2. The source path recorded on the page's tree — root prop first, then any
//      descendant node's sourcePath. This is what the Create flow writes at
//      build time (AddVisualPage / courseTemplate) and directly encodes the
//      type via its courses/ | conferences/ prefix. Definitive for every page
//      built from a source (all conferences; courses built from a course page).
//   3. A course-scoped sourceNid binding when no source path exists anywhere:
//      the sourceNid mechanism is course-only by construction — PageEditor's
//      useDynamicPage resolves every sourceNid as { type: 'course' } and all
//      dynamic blocks read coursepages/* endpoints, while conference trees
//      always carry a conferences/ sourcePath. So a tree bound only by a
//      numeric sourceNid is a course page.
export function deriveVisualPageType(currentPage, tree) {
  const stored = currentPage && currentPage.page_type;
  if (isValidVisualPageType(stored)) return stored;

  // Single depth-first walk: a sourcePath is definitive and returns immediately;
  // a numeric sourceNid is remembered as the course-only fallback (step 3).
  let sawCourseNid = false;
  const visit = (node) => {
    if (!node || typeof node !== 'object') return '';
    const props = node.props || {};
    const fromPath = pageTypeFromSourcePath(props.sourcePath);
    if (fromPath) return fromPath;
    if (props.sourceNid != null && String(props.sourceNid).trim() !== '' && Number(props.sourceNid)) {
      sawCourseNid = true;
    }
    const kids = Array.isArray(node.children) ? node.children : [];
    for (let i = 0; i < kids.length; i += 1) {
      const hit = visit(kids[i]);
      if (hit) return hit;
    }
    return '';
  };
  const fromTree = visit(tree || {});
  if (fromTree) return fromTree;
  if (sawCourseNid) return 'course';
  return '';
}
